import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Users, Search, Loader2, AlertTriangle, MapPin, Check, X as XIcon,
  CalendarDays, ChevronDown, ChevronUp, Wifi, WifiOff, Pencil, Trash2, ShieldCheck,
} from 'lucide-react';
import {
  searchWorkers, getCompanyWorkers, getEventsByRange,
  getWorkplaces, EVENT_TYPES, EVENT_TYPE_LABELS, APPROVAL_STATUS, APPROVAL_STATUS_LABELS,
  isDeferred, approverLabel, modifierLabel, parseMotivo, parseRecoverableFlag,
} from '../services/api';
import { filterWorkersForUser, USER_TYPE_LABELS, USER_TYPES, canDeleteEvent } from '../lib/permissions';
import { filterEventsToLocalRange } from '../lib/dayStatus';
import { computeDayWork, pauseWarningsByEvent, PAUSE_WARNING_LABELS } from '../lib/hourBalance';
import EventEditModal from './EventEditModal';
import DeleteEventsConfirmModal from './DeleteEventsConfirmModal';
import {
  DEFAULT_TIME_ZONE, timeZoneForWorker, timeZoneLabel, dayKeyInZone, formatHmInZone,
  todayInZone, addDaysToDayKey, dowOfDayKey, dayKeysBetween,
} from '../lib/workerTime';

// ─── helpers ───────────────────────────────────────────────────────────────
// Atajos de rango como claves de día en la zona `tz` (la del usuario que
// consulta). Aritmética pura de claves, sin la zona del navegador.
const todayIso = (tz) => todayInZone(tz);
const weekStartIso = (tz) => {
  const t = todayInZone(tz);
  return addDaysToDayKey(t, -((dowOfDayKey(t) + 6) % 7));
};
const monthStartIso = (tz) => `${todayInZone(tz).slice(0, 8)}01`;
const monthsAgoIso = (months, tz) => {
  const t = todayInZone(tz);
  const d = new Date(Date.UTC(Number(t.slice(0, 4)), Number(t.slice(5, 7)) - 1 - months, Number(t.slice(8, 10))));
  return d.toISOString().slice(0, 10);
};
// Fin +1 día: el backend ANTIGUO cortaba el día final a las 00:00Z. El actual
// es inclusivo; el resultado se recorta con filterEventsToLocalRange.
const nextDayIso = (iso) => (iso ? addDaysToDayKey(iso, 1) : iso);
// Hora de reloj en la zona del trabajador (para que hora y día cuadren).
const fmtTime = (iso, tz = DEFAULT_TIME_ZONE) => (iso ? formatHmInZone(iso, tz) || null : null);
// `iso` = clave de día: se pinta en UTC para que el navegador no la desplace.
const fmtDay = (iso) => new Date(`${iso}T00:00:00Z`).toLocaleDateString('es-ES', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });
const fmtHours = (mins) => !mins ? '0h 00m' : `${Math.floor(mins / 60)}h ${String(mins % 60).padStart(2, '0')}m`;

const initials = (nombre = '') =>
  nombre.split(' ').map((p) => p[0]).filter(Boolean).slice(0, 2).join('').toUpperCase() || '?';

const AVATAR_COLORS = [
  'bg-primary-soft text-primary-darker',
  'bg-accent-light text-accent-dark',
  'bg-success/15 text-success',
  'bg-warning/15 text-warning',
  'bg-danger/15 text-danger',
];
const colorForId = (id) => AVATAR_COLORS[Math.abs(Number(id) || 0) % AVATAR_COLORS.length];

// Trabajado y pausas salen de computeDayWork (src/lib/hourBalance.js):
// NINGUNA pausa cuenta como trabajada, todas se restan (decisión de producto
// 2026-09-21), igual que la bolsa. Antes aquí NO se restaba ninguna pausa.
// `tz`: zona del trabajador — decide a qué día pertenece cada fichaje.
const groupEventsByDay = (events, tz = DEFAULT_TIME_ZONE) => {
  const map = new Map();
  for (const e of events) {
    const fecha = dayKeyInZone(e.timestamp, tz);
    if (!fecha) continue;
    if (!map.has(fecha)) map.set(fecha, { fecha, ins: [], outs: [], events: [] });
    const day = map.get(fecha);
    day.events.push(e);
    if (e.eventType === EVENT_TYPES.CLOCK_IN) day.ins.push(e.timestamp);
    else if (e.eventType === EVENT_TYPES.CLOCK_OUT) day.outs.push(e.timestamp);
  }
  return Array.from(map.values()).map((d) => {
    d.ins.sort(); d.outs.sort();
    d.entrada = d.ins[0] || null;
    d.salida = d.outs[d.outs.length - 1] || null;
    d.work = computeDayWork(d.events, { timeZone: tz });
    d.totalMinutes = d.work.workedMinutes;
    d.pauseMinutes = d.work.pauseMinutes;
    d.pauseWarnings = pauseWarningsByEvent(d.work);
    d.clockEvents = d.events
      .filter((e) => e.eventType === EVENT_TYPES.CLOCK_IN || e.eventType === EVENT_TYPES.CLOCK_OUT)
      .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    d.allEvents = [...d.events].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    return d;
  }).sort((a, b) => b.fecha.localeCompare(a.fecha));
};

const badgeClsForType = (type) => {
  if (type === EVENT_TYPES.CLOCK_IN) return 'bg-success/10 text-success';
  if (type === EVENT_TYPES.CLOCK_OUT) return 'bg-primary-soft text-primary-darker';
  if (type === EVENT_TYPES.PAUSE_START || type === EVENT_TYPES.PAUSE_END) return 'bg-amber-100 text-amber-800';
  if (type === EVENT_TYPES.VACATION_START || type === EVENT_TYPES.VACATION_END) return 'bg-sky-100 text-sky-800';
  if (type === EVENT_TYPES.SICK_LEAVE_START || type === EVENT_TYPES.SICK_LEAVE_END) return 'bg-danger/10 text-danger';
  if (type === EVENT_TYPES.COMPENSATION_START || type === EVENT_TYPES.COMPENSATION_END) return 'bg-violet-100 text-violet-800';
  if (type === EVENT_TYPES.ABSENCE_START || type === EVENT_TYPES.ABSENCE_END) return 'bg-orange-100 text-orange-800';
  return 'bg-slate-100 text-ink-muted';
};

// Construye un Set con TODAS las fechas (ISO local) que caen dentro de un
// período aprobado/pendiente de tipo (startType, endType). Empareja eventos
// y rellena el rango día a día. Sirve para pintar los días de la tabla:
//   · vacaciones → rosa pastel
//   · bajas      → rojo claro
//   · ausencias  → naranja claro
// Eventos sin pareja (start abierto) marcan solo su propio día.
// Días en la zona del trabajador (`tz`).
const buildPeriodDaySet = (events, startType, endType, tz = DEFAULT_TIME_ZONE) => {
  const sorted = events
    .filter((e) => e.eventType === startType || e.eventType === endType)
    .sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  const days = new Set();
  let pending = null;
  for (const e of sorted) {
    if (e.eventType === startType) pending = e;
    else if (e.eventType === endType && pending) {
      for (const k of dayKeysBetween(dayKeyInZone(pending.timestamp, tz), dayKeyInZone(e.timestamp, tz))) days.add(k);
      pending = null;
    }
  }
  if (pending) {
    const k = dayKeyInZone(pending.timestamp, tz);
    if (k) days.add(k);
  }
  return days;
};

// Estilo de fondo para la celda del día según tipo de evento que cubre ese
// día. Prioridad: baja > vacación > ausencia > compensación > normal.
// Si solapan, gana el más "grave" para que no se camufle un período que
// requiere atención. Devolvemos también el label corto para el tooltip.
const cellStyleForDay = (iso, sets) => {
  if (sets.sick.has(iso))        return { cls: 'bg-danger/10 text-danger',     label: 'Día completo' };
  if (sets.vacation.has(iso))    return { cls: 'bg-pink-100 text-pink-800',    label: 'Vacaciones' };
  if (sets.absence.has(iso))     return { cls: 'bg-orange-100 text-orange-800', label: 'Por horas' };
  if (sets.compensation.has(iso))return { cls: 'bg-violet-100 text-violet-800', label: 'Día de compensación' };
  return null;
};

// Tipos de evento "supervisados" — para esos mostramos quién aprobó/rechazó.
// Los CLOCK/PAUSE normalmente no pasan por aprobación, pero por completitud
// también enseñamos el label si lo traen (p.ej. un fichaje diferido).
const SUPERVISED_TYPES = new Set([
  EVENT_TYPES.VACATION_START, EVENT_TYPES.VACATION_END,
  EVENT_TYPES.SICK_LEAVE_START, EVENT_TYPES.SICK_LEAVE_END,
  EVENT_TYPES.COMPENSATION_START, EVENT_TYPES.COMPENSATION_END,
  EVENT_TYPES.ABSENCE_START, EVENT_TYPES.ABSENCE_END,
]);

const InformeHorarioView = ({ user }) => {
  const isMaster = user?.type === USER_TYPES.MECHAGODZILLA;
  const isWorker = user?.type === USER_TYPES.WORKER;
  // Zona de quien consulta: solo para los atajos de rango ("hoy", "semana"…).
  // Cada trabajador consultado se agrupa y pinta en SU zona (ver `results`).
  const viewerTz = timeZoneForWorker(user);

  // ── Datos base ─────────────────────────────────────────────────────────
  const [workers, setWorkers] = useState([]);
  const [workplaces, setWorkplaces] = useState([]);
  const [loadingBase, setLoadingBase] = useState(true);
  const [errorBase, setErrorBase] = useState(null);

  // ── Filtros del panel izquierdo ────────────────────────────────────────
  const [workplaceFilter, setWorkplaceFilter] = useState('todos');
  const [query, setQuery] = useState('');
  // Para trabajador (rol 0): siempre se preselecciona él mismo y no se puede
  // tocar el panel. Para admins: arranca vacío y eligen libremente.
  const [selectedIds, setSelectedIds] = useState(isWorker && user?.id ? [user.id] : []);

  // ── Rango de fechas (draft/applied para que los inputs no recarguen) ──
  const [draftStart, setDraftStart] = useState(() => weekStartIso(viewerTz));
  const [draftEnd, setDraftEnd] = useState(() => todayIso(viewerTz));
  const [appliedStart, setAppliedStart] = useState(() => weekStartIso(viewerTz));
  const [appliedEnd, setAppliedEnd] = useState(() => todayIso(viewerTz));

  // ── Resultados ─────────────────────────────────────────────────────────
  const [eventsByWorker, setEventsByWorker] = useState({});
  const [fetching, setFetching] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);

  // ── UI ──────────────────────────────────────────────────────────────────
  const [expanded, setExpanded] = useState(new Set());
  const isExpanded = (wId, fecha) => expanded.has(`${wId}:${fecha}`);
  const toggleExpanded = (wId, fecha) => setExpanded((prev) => {
    const k = `${wId}:${fecha}`;
    const next = new Set(prev);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });

  const [editingEvent, setEditingEvent] = useState(null);
  const [deleteState, setDeleteState] = useState(null);

  const handleEdited = (updated) => {
    if (!updated?.id || !editingEvent?.workerId) return;
    const wId = editingEvent.workerId;
    setEventsByWorker((prev) => ({
      ...prev,
      [wId]: (prev[wId] || []).map((e) => (e.id === updated.id ? { ...e, ...updated } : e)),
    }));
  };
  const handleDeleted = (deletedIds) => {
    if (!deletedIds?.length || !deleteState?.workerId) return;
    const wId = deleteState.workerId;
    setEventsByWorker((prev) => ({
      ...prev,
      [wId]: (prev[wId] || []).filter((e) => !deletedIds.includes(e.id)),
    }));
  };

  // ── Carga inicial ──────────────────────────────────────────────────────
  const loadBase = useCallback(async () => {
    setLoadingBase(true);
    setErrorBase(null);
    if (isWorker) {
      // Trabajador: no necesitamos la lista de compañeros. Solo nos vemos
      // a nosotros mismos — el panel queda invisible (ver render abajo).
      setWorkers([user]);
      setLoadingBase(false);
      return;
    }
    try {
      const data = (isMaster || !user?.companyId)
        ? await searchWorkers('')
        : await getCompanyWorkers(user.companyId);
      setWorkers(filterWorkersForUser(Array.isArray(data) ? data : [], user));
    } catch (err) {
      setErrorBase(err);
      setWorkers([]);
    }
    if (!isMaster && user?.companyId) {
      try {
        const wps = await getWorkplaces(user.companyId);
        setWorkplaces(Array.isArray(wps) ? wps : []);
      } catch {
        setWorkplaces([]);
      }
    } else {
      setWorkplaces([]);
    }
    setLoadingBase(false);
  }, [user, isMaster, isWorker]);

  useEffect(() => { loadBase(); }, [loadBase]);

  // Auto-fetch para trabajador (no tiene panel, queremos que vea sus
  // datos en cuanto entra a la pestaña con el rango por defecto).
  useEffect(() => {
    if (!isWorker || !user?.id) return;
    // Se dispara una vez con el rango inicial. Después el admin/trabajador
    // pulsa "Consultar" para refrescar.
    runQuery();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWorker, user?.id]);

  // ── Lista filtrada del panel ───────────────────────────────────────────
  const filteredWorkers = useMemo(() => {
    const q = query.trim().toLowerCase();
    return workers.filter((w) => {
      if (workplaceFilter !== 'todos') {
        const wpId = w.workingSite != null ? String(w.workingSite) : '';
        if (workplaceFilter === 'sin-sede') {
          if (wpId) return false;
        } else if (wpId !== String(workplaceFilter)) {
          return false;
        }
      }
      if (q) {
        const hay = `${w.name || ''} ${w.email || ''} ${w.nif || ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [workers, workplaceFilter, query]);

  const toggleWorker = (id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };
  const selectAllFiltered = () => {
    setSelectedIds((prev) => {
      const set = new Set(prev);
      for (const w of filteredWorkers) set.add(w.id);
      return Array.from(set);
    });
  };
  const clearSelection = () => setSelectedIds([]);

  // ── Rango ──────────────────────────────────────────────────────────────
  const applyRange = (start, end) => {
    setDraftStart(start);
    setDraftEnd(end);
    setAppliedStart(start);
    setAppliedEnd(end);
    setHasFetched(false);
  };
  const setQuick = (key) => {
    const t = todayIso(viewerTz);
    if (key === 'hoy') applyRange(t, t);
    else if (key === 'semana') applyRange(weekStartIso(viewerTz), t);
    else if (key === 'mes') applyRange(monthStartIso(viewerTz), t);
    else if (key === '3meses') applyRange(monthsAgoIso(3, viewerTz), t);
    else if (key === 'anio') applyRange(monthsAgoIso(12, viewerTz), t);
  };

  // ── Buscar (lanza fetch) ───────────────────────────────────────────────
  const runQuery = useCallback(async () => {
    if (!selectedIds.length) return;
    const s = draftStart, e = draftEnd;
    setAppliedStart(s);
    setAppliedEnd(e);
    setFetching(true);
    setFetchError(null);
    const out = {};
    try {
      await Promise.all(selectedIds.map(async (id) => {
        try {
          // [s - 1, e + 1]: el día de Madrid empieza a las 22:00/23:00 UTC
          // del anterior (y e+1 cubre el backend antiguo, fin exclusivo).
          const list = await getEventsByRange(id, addDaysToDayKey(s, -1) || s, nextDayIso(e));
          // Recorte al rango INCLUSIVO [s, e] por día en la zona del
          // TRABAJADOR consultado.
          const w = workers.find((x) => Number(x.id) === Number(id));
          out[id] = filterEventsToLocalRange(list, s, e, timeZoneForWorker(w));
        } catch {
          out[id] = [];
        }
      }));
      setEventsByWorker(out);
      setHasFetched(true);
    } catch (err) {
      setFetchError(err);
    } finally {
      setFetching(false);
    }
  }, [selectedIds, draftStart, draftEnd, workers]);

  // Estado del botón "Consultar": resaltado cuando algo cambió respecto a
  // lo último consultado.
  const draftDirty = draftStart !== appliedStart || draftEnd !== appliedEnd;
  const selectionChanged = (() => {
    if (!hasFetched) return selectedIds.length > 0;
    const fetchedIds = Object.keys(eventsByWorker).map(Number);
    if (fetchedIds.length !== selectedIds.length) return true;
    const set = new Set(fetchedIds);
    return selectedIds.some((id) => !set.has(id));
  })();
  // Validamos el rango aquí (no en el input) — los inputs son libres para
  // permitir poner desde/hasta en futuro o pasado sin restricciones.
  const rangeValid = draftStart && draftEnd && draftStart <= draftEnd;
  const canSearch = selectedIds.length > 0 && !fetching && rangeValid;
  const searchHighlight = canSearch && (draftDirty || selectionChanged || !hasFetched);

  // ── Datos derivados ────────────────────────────────────────────────────
  const workerById = useMemo(() => {
    const m = new Map();
    for (const w of workers) m.set(w.id, w);
    return m;
  }, [workers]);
  const workplaceById = useMemo(() => {
    const m = new Map();
    for (const wp of workplaces) m.set(String(wp.id), wp);
    return m;
  }, [workplaces]);

  const results = useMemo(() => {
    return Object.entries(eventsByWorker).map(([id, events]) => {
      const worker = workerById.get(Number(id));
      // Cada trabajador en SU zona (Canarias / península).
      const tz = timeZoneForWorker(worker);
      const tzHint = timeZoneLabel(tz);
      const days = groupEventsByDay(events, tz);
      const totalMinutes = days.reduce((s, d) => s + d.totalMinutes, 0);
      const workedDays = days.filter((d) => d.totalMinutes > 0).length;
      const pendingCount = events.filter((e) => Number(e.approvalStatus ?? 1) === APPROVAL_STATUS.PENDING).length;
      const deferredCount = events.filter(isDeferred).length;
      // Sets de fechas por tipo de período — para colorear las celdas del
      // día en la tabla (vacaciones rosa, bajas rojo, ausencias naranja…).
      // Solo periodos APROBADOS o PENDIENTES; los rechazados no cuentan.
      const valid = events.filter((e) => Number(e.approvalStatus ?? 1) !== APPROVAL_STATUS.REJECTED);
      const daySets = {
        vacation: buildPeriodDaySet(valid, EVENT_TYPES.VACATION_START, EVENT_TYPES.VACATION_END, tz),
        sick: buildPeriodDaySet(valid, EVENT_TYPES.SICK_LEAVE_START, EVENT_TYPES.SICK_LEAVE_END, tz),
        absence: buildPeriodDaySet(valid, EVENT_TYPES.ABSENCE_START, EVENT_TYPES.ABSENCE_END, tz),
        compensation: buildPeriodDaySet(valid, EVENT_TYPES.COMPENSATION_START, EVENT_TYPES.COMPENSATION_END, tz),
      };
      return { id: Number(id), worker, tz, tzHint, days, totalMinutes, workedDays, pendingCount, deferredCount, daySets };
    }).sort((a, b) => (a.worker?.name || '').localeCompare(b.worker?.name || '', 'es'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventsByWorker, workerById]);

  const summary = useMemo(() => {
    const totalMinutes = results.reduce((s, r) => s + r.totalMinutes, 0);
    const totalEvents = results.reduce((s, r) => s + r.days.reduce((ss, d) => ss + d.events.length, 0), 0);
    const totalPending = results.reduce((s, r) => s + r.pendingCount, 0);
    const totalDeferred = results.reduce((s, r) => s + r.deferredCount, 0);
    return { totalMinutes, totalEvents, totalPending, totalDeferred };
  }, [results]);

  // Si solo hay UN trabajador consultado, no tiene sentido repetir su nombre
  // en el header de la tabla — el contexto ya es claro. Si hay 2+, sí.
  const showWorkerHeader = results.length > 1;

  // ── Render ─────────────────────────────────────────────────────────────
  // Layout:
  //   · Admin (≥1): panel izquierdo (selector trabajadores + sede) + tabla.
  //   · Trabajador (rol 0): solo tabla (sin panel), auto-cargada al entrar.
  const showPanel = !isWorker;

  return (
    <div className="flex-1 min-h-0 flex flex-col">
      {errorBase && (
        <div className="border border-danger/30 bg-danger/5 rounded-xl p-3 mb-4 flex items-start gap-3">
          <AlertTriangle size={16} className="text-danger shrink-0 mt-0.5" />
          <div className="flex-1 text-sm font-semibold text-ink">{errorBase.message}</div>
        </div>
      )}

      <div className={`flex-1 min-h-0 grid gap-5 overflow-hidden ${showPanel ? 'lg:grid-cols-[300px_1fr]' : 'grid-cols-1'}`}>
        {/* ───────── Panel izquierdo: selectores (solo admins) ───────── */}
        {showPanel && (
          <aside className="flex flex-col min-h-0 border border-slate-200 rounded-xl bg-white">
            <div className="p-3 border-b border-slate-200 space-y-2.5">
              <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold inline-flex items-center gap-2">
                <Users size={12} /> Trabajadores
                <span className="text-ink font-bold tabular-nums ml-auto">
                  {selectedIds.length}/{filteredWorkers.length}
                </span>
              </div>

              {workplaces.length > 1 && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-ink-muted font-semibold mb-1 inline-flex items-center gap-1">
                    <MapPin size={10} /> Sede
                  </div>
                  <select
                    value={workplaceFilter}
                    onChange={(e) => setWorkplaceFilter(e.target.value)}
                    className="w-full px-2 py-1.5 border border-slate-200 rounded-md text-xs focus:outline-none focus:border-primary bg-white"
                  >
                    <option value="todos">Todas las sedes</option>
                    <option value="sin-sede">Sin sede asignada</option>
                    {workplaces.map((wp) => (
                      <option key={wp.id} value={wp.id}>{wp.name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-ink-muted" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Buscar por nombre, email, NIF…"
                  className="pl-8 pr-2 py-1.5 border border-slate-200 rounded-md focus:outline-none focus:border-primary text-xs w-full"
                />
              </div>

              <div className="flex items-center justify-between gap-2 text-xs">
                <div className="flex items-center gap-2">
                  <button onClick={selectAllFiltered} className="text-primary font-semibold hover:underline">
                    Seleccionar todos
                  </button>
                  <span className="text-slate-300">·</span>
                  <button onClick={clearSelection} className="text-ink-muted font-semibold hover:text-ink">
                    Ninguno
                  </button>
                </div>
              </div>
            </div>

            <ul className="flex-1 overflow-y-auto divide-y divide-slate-100">
              {loadingBase ? (
                <li className="py-10 text-center text-sm text-ink-muted inline-flex items-center justify-center gap-2 w-full">
                  <Loader2 size={14} className="animate-spin" /> Cargando…
                </li>
              ) : filteredWorkers.length === 0 ? (
                <li className="py-10 text-center text-xs text-ink-muted">
                  {workers.length === 0 ? 'Sin trabajadores.' : 'Ningún trabajador coincide con los filtros.'}
                </li>
              ) : (
                filteredWorkers.map((w) => {
                  const selected = selectedIds.includes(w.id);
                  const wp = workplaceById.get(String(w.workingSite));
                  return (
                    <li key={w.id}>
                      <button
                        onClick={() => toggleWorker(w.id)}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm transition-colors ${
                          selected ? 'bg-primary-soft' : 'hover:bg-slate-50'
                        }`}
                      >
                        <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                          selected ? 'bg-primary border-primary' : 'border-slate-300 bg-white'
                        }`}>
                          {selected && <Check size={11} className="text-white" />}
                        </span>
                        <span className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 ${colorForId(w.id)}`}>
                          {initials(w.name)}
                        </span>
                        <span className="flex-1 min-w-0">
                          <div className="font-semibold text-ink truncate text-xs">{w.name}</div>
                          <div className="text-[10px] text-ink-muted truncate">
                            {wp?.name || (w.workingSite ? 'sede #' + w.workingSite : 'sin sede')}
                            {' · '}
                            {USER_TYPE_LABELS[w.type] || 'trabajador'}
                          </div>
                        </span>
                        {w.worksRemotely
                          ? <Wifi size={11} className="text-success shrink-0" title="Remoto" />
                          : <WifiOff size={11} className="text-ink-muted/50 shrink-0" title="Presencial" />
                        }
                      </button>
                    </li>
                  );
                })
              )}
            </ul>
          </aside>
        )}

        {/* ───────── Resultados ───────── */}
        <section className="flex flex-col min-h-0 border border-slate-200 rounded-xl bg-white">
          <div className="p-3 border-b border-slate-200 space-y-2 shrink-0">
            <div className="flex flex-wrap items-center gap-2">
              <div className="text-xs uppercase tracking-wider text-ink-muted font-semibold inline-flex items-center gap-2 shrink-0">
                <CalendarDays size={12} /> Período
              </div>
              {/* Sin min/max: permitimos consultar tanto pasado como
                  futuro (útil para revisar vacaciones programadas). Si el
                  admin pone "desde" > "hasta", el botón Consultar se
                  deshabilita más abajo y le sale un tooltip. */}
              <input
                type="date"
                value={draftStart}
                onChange={(e) => setDraftStart(e.target.value)}
                className="px-2 py-1 border border-slate-200 rounded-md text-xs focus:outline-none focus:border-primary"
              />
              <span className="text-xs text-ink-muted">→</span>
              <input
                type="date"
                value={draftEnd}
                onChange={(e) => setDraftEnd(e.target.value)}
                className="px-2 py-1 border border-slate-200 rounded-md text-xs focus:outline-none focus:border-primary"
              />
              <div className="flex items-center gap-0.5">
                {[
                  { k: 'hoy', l: 'Hoy' },
                  { k: 'semana', l: 'Esta sem.' },
                  { k: 'mes', l: 'Este mes' },
                  { k: '3meses', l: '3 meses' },
                  { k: 'anio', l: '1 año' },
                ].map((opt) => (
                  <button
                    key={opt.k}
                    type="button"
                    onClick={() => setQuick(opt.k)}
                    className="px-2 py-1 rounded-md text-[11px] font-semibold text-ink-muted hover:text-ink hover:bg-slate-100 transition-colors"
                  >
                    {opt.l}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={runQuery}
                disabled={!canSearch}
                className={`ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition-colors ${
                  !canSearch
                    ? 'bg-slate-100 text-ink-muted/60 cursor-not-allowed'
                    : searchHighlight
                      ? 'bg-primary text-white hover:bg-primary-dark shadow-sm'
                      : 'bg-slate-200 text-ink hover:bg-slate-300'
                }`}
                title={
                  selectedIds.length === 0
                    ? 'Selecciona al menos un trabajador'
                    : !rangeValid
                      ? 'La fecha "desde" debe ser igual o anterior a "hasta"'
                      : 'Consultar fichajes en el período indicado'
                }
              >
                {fetching ? <Loader2 size={12} className="animate-spin" /> : <Search size={12} />}
                Consultar
              </button>
            </div>

            {hasFetched && results.length > 0 && (
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-ink-muted">
                <span className="tabular-nums"><b className="text-ink">{results.length}</b> trabajador{results.length === 1 ? '' : 'es'}</span>
                <span className="text-slate-300">·</span>
                <span className="tabular-nums"><b className="text-ink">{results.reduce((s, r) => s + r.workedDays, 0)}</b> jornadas</span>
                <span className="text-slate-300">·</span>
                <span className="tabular-nums"><b className="text-ink">{summary.totalEvents}</b> fichajes</span>
                <span className="text-slate-300">·</span>
                <span className="tabular-nums"><b className="text-ink">{fmtHours(summary.totalMinutes)}</b> totales</span>
                {summary.totalPending > 0 && (
                  <>
                    <span className="text-slate-300">·</span>
                    <span className="tabular-nums text-amber-600 font-semibold">{summary.totalPending} pendiente{summary.totalPending === 1 ? '' : 's'} de aprobar</span>
                  </>
                )}
                {summary.totalDeferred > 0 && (
                  <>
                    <span className="text-slate-300">·</span>
                    <span className="tabular-nums text-amber-700 font-semibold">{summary.totalDeferred} diferido{summary.totalDeferred === 1 ? '' : 's'}</span>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto p-3">
            {fetchError && (
              <div className="border border-danger/30 bg-danger/5 rounded-xl p-3 mb-3 flex items-start gap-3">
                <AlertTriangle size={16} className="text-danger shrink-0 mt-0.5" />
                <div className="flex-1 text-sm font-semibold text-ink">{fetchError.message || 'Error al consultar.'}</div>
              </div>
            )}

            {!hasFetched && !fetching && (
              <div className="h-full flex items-center justify-center">
                <div className="text-center text-sm text-ink-muted max-w-md">
                  <div className="mx-auto mb-3 w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center">
                    <Search size={20} className="text-ink-muted/70" />
                  </div>
                  <div className="font-semibold text-ink mb-1">Informe horario</div>
                  <div className="text-xs">
                    {showPanel
                      ? <>Selecciona uno o varios trabajadores en el panel de la izquierda, elige un período y pulsa <b>Consultar</b>.</>
                      : <>Elige un período y pulsa <b>Consultar</b> para ver tus fichajes.</>
                    }
                  </div>
                </div>
              </div>
            )}

            {fetching && (
              <div className="h-full flex items-center justify-center text-ink-muted gap-2">
                <Loader2 size={16} className="animate-spin" />
                <span className="text-sm">Consultando fichajes…</span>
              </div>
            )}

            {hasFetched && !fetching && results.length === 0 && (
              <div className="h-full flex items-center justify-center text-sm text-ink-muted text-center">
                Sin fichajes en este período.
              </div>
            )}

            {!fetching && results.length > 0 && (
              <div className="space-y-3">
                {results.map((r) => (
                  <div key={r.id} className="border border-slate-200 rounded-xl overflow-hidden">
                    {/* Cabecera del trabajador SOLO si hay >1 seleccionado.
                        Cuando es 1 solo no aporta info y roba espacio. */}
                    {showWorkerHeader && (
                      <div className="px-3 py-2.5 bg-slate-50/60 border-b border-slate-200 flex items-center gap-3 flex-wrap">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${colorForId(r.id)}`}>
                          {initials(r.worker?.name)}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-ink truncate">
                            {r.worker?.name || `Trabajador #${r.id}`}
                          </div>
                          <div className="text-[10px] text-ink-muted truncate">
                            {(() => {
                              const wp = workplaceById.get(String(r.worker?.workingSite));
                              return wp?.name || (r.worker?.workingSite ? `sede #${r.worker.workingSite}` : 'sin sede');
                            })()}
                            {' · '}
                            {USER_TYPE_LABELS[r.worker?.type] || 'trabajador'}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 text-[11px] flex-wrap">
                          <span className="tabular-nums text-ink-muted"><b className="text-ink">{r.workedDays}</b> jornadas</span>
                          <span className="tabular-nums text-ink-muted"><b className="text-ink">{fmtHours(r.totalMinutes)}</b></span>
                          {r.pendingCount > 0 && (
                            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-bold uppercase tracking-wider">
                              {r.pendingCount} pendiente{r.pendingCount === 1 ? '' : 's'}
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {r.days.length === 0 ? (
                      <div className="py-6 text-center text-xs text-ink-muted">
                        Sin fichajes en el período seleccionado.
                      </div>
                    ) : (
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="text-[10px] uppercase tracking-wider text-ink-muted">
                            <th className="text-left font-semibold py-2 px-3 w-7"></th>
                            <th className="text-left font-semibold py-2 px-3">Día</th>
                            <th className="text-left font-semibold py-2 px-3">
                              Entrada
                              {r.tzHint && <span className="ml-1 normal-case tracking-normal font-normal">· {r.tzHint}</span>}
                            </th>
                            <th className="text-left font-semibold py-2 px-3">Salida</th>
                            <th className="text-right font-semibold py-2 px-3">Fichajes</th>
                            <th className="text-right font-semibold py-2 px-3" title="Tiempo total de pausas del día (se resta del total)">Pausas</th>
                            <th className="text-right font-semibold py-2 px-3">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {r.days.map((d) => {
                            const open = isExpanded(r.id, d.fecha);
                            // Color de la celda del día según el período
                            // (vacación, baja, ausencia, compensación). Solo
                            // se pinta la columna del día — el resto de la
                            // fila no, para no romper la legibilidad de los
                            // tiempos.
                            const dayStyle = cellStyleForDay(d.fecha, r.daySets);
                            return (
                              <React.Fragment key={d.fecha}>
                                <tr
                                  className={`border-t border-slate-100 cursor-pointer transition-colors ${
                                    open ? 'bg-primary-soft/20' : 'hover:bg-slate-50'
                                  }`}
                                  onClick={() => toggleExpanded(r.id, d.fecha)}
                                >
                                  <td className="py-2 px-3 text-ink-muted">
                                    {open ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                                  </td>
                                  <td
                                    className={`py-2 px-3 capitalize font-medium ${dayStyle ? `${dayStyle.cls} rounded` : 'text-ink'}`}
                                    title={dayStyle?.label}
                                  >
                                    {fmtDay(d.fecha)}
                                    {dayStyle && (
                                      <span className="ml-1.5 text-[9px] uppercase tracking-wider font-bold opacity-70">
                                        · {dayStyle.label}
                                      </span>
                                    )}
                                  </td>
                                  <td className="py-2 px-3 tabular-nums text-success font-semibold">
                                    {fmtTime(d.entrada, r.tz) || <span className="text-ink-muted font-normal">—</span>}
                                  </td>
                                  <td className="py-2 px-3 tabular-nums text-primary-darker font-semibold">
                                    {fmtTime(d.salida, r.tz) || <span className="text-ink-muted font-normal">—</span>}
                                  </td>
                                  <td className="py-2 px-3 text-right tabular-nums text-ink-muted">
                                    {d.ins.length}↑ · {d.outs.length}↓
                                    {d.allEvents.length > d.clockEvents.length && (
                                      <span className="ml-1 text-amber-700">
                                        +{d.allEvents.length - d.clockEvents.length}
                                      </span>
                                    )}
                                  </td>
                                  <td className="py-2 px-3 text-right tabular-nums text-amber-700 font-medium">
                                    {d.pauseMinutes > 0 ? fmtHours(d.pauseMinutes) : <span className="text-ink-muted font-normal">—</span>}
                                    {d.work.hasPauseWarning && (
                                      <span
                                        className="ml-1 inline-flex items-center align-middle text-warning"
                                        title="Pausa sin cerrar o sin inicio: revisa los fichajes de este día"
                                      >
                                        <AlertTriangle size={11} />
                                      </span>
                                    )}
                                  </td>
                                  <td className="py-2 px-3 text-right tabular-nums font-bold text-ink">
                                    {d.totalMinutes > 0 ? fmtHours(d.totalMinutes) : <span className="text-ink-muted font-normal">—</span>}
                                  </td>
                                </tr>
                                {open && d.allEvents.length > 0 && (
                                  <tr className="border-t border-slate-100 bg-primary-soft/10">
                                    <td></td>
                                    <td colSpan={6} className="py-2 px-3">
                                      <ul className="space-y-1">
                                        {d.allEvents.map((e) => {
                                          const status = Number(e.approvalStatus ?? APPROVAL_STATUS.APPROVED);
                                          const canEdit = canDeleteEvent(user, r.worker);
                                          // "Aprobado por X" — solo lo
                                          // enseñamos en eventos supervisados
                                          // (vacaciones, bajas, ausencias,
                                          // compensaciones) o en cualquier
                                          // evento diferido (donde el admin
                                          // también tuvo que dar el visto bueno).
                                          const showApprover =
                                            SUPERVISED_TYPES.has(e.eventType) || isDeferred(e);
                                          const apprText = showApprover ? approverLabel(e) : null;
                                          // Flag de "recuperable" en ausencias justificadas
                                          // (ABSENCE_* / SICK_LEAVE_*). El admin lo elige al
                                          // aprobar — true = horas que el trabajador debe a la
                                          // empresa (descontadas de la bolsa). false = la
                                          // empresa asume la ausencia. null = aún sin decidir
                                          // (evento pendiente o anterior al cambio).
                                          // Por horas: se recuperan SIEMPRE (2026-09-22),
                                          // aunque un aprobado antiguo diga ;recoverable=0.
                                          const isHourlyAbsence = e.eventType === EVENT_TYPES.ABSENCE_START || e.eventType === EVENT_TYPES.ABSENCE_END;
                                          const recoverable = isHourlyAbsence && status === APPROVAL_STATUS.APPROVED
                                            ? true
                                            : parseRecoverableFlag(e.geoInfo);
                                          // El "Editado por X" lo enseñamos
                                          // en CUALQUIER evento que haya pasado
                                          // por EventEditModal — no importa
                                          // el tipo. Si no fue editado, no hay
                                          // flag y no se muestra nada.
                                          const modText = modifierLabel(e);
                                          const motivo = parseMotivo(e.geoInfo);
                                          return (
                                            <li key={e.id} className="flex items-center gap-2 text-[11px] flex-wrap">
                                              <span className={`px-1.5 py-0.5 rounded-full font-semibold ${badgeClsForType(e.eventType)}`}>
                                                {EVENT_TYPE_LABELS[e.eventType] || `Tipo ${e.eventType}`}
                                              </span>
                                              <span className="tabular-nums font-mono text-ink">{fmtTime(e.timestamp, r.tz)}</span>
                                              {d.pauseWarnings.has(e) && (
                                                <span
                                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-bold uppercase tracking-wider"
                                                  title="Esta pausa no se empareja bien con el resto de fichajes del día"
                                                >
                                                  <AlertTriangle size={10} /> {PAUSE_WARNING_LABELS[d.pauseWarnings.get(e)]}
                                                </span>
                                              )}
                                              {status !== APPROVAL_STATUS.APPROVED && (
                                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${
                                                  status === APPROVAL_STATUS.PENDING ? 'bg-amber-100 text-amber-800' : 'bg-danger/10 text-danger'
                                                }`}>
                                                  {APPROVAL_STATUS_LABELS[status]}
                                                </span>
                                              )}
                                              {isDeferred(e) && (
                                                <span className="inline-flex items-center px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-bold uppercase tracking-wider">
                                                  Diferido
                                                </span>
                                              )}
                                              {motivo && (
                                                <span
                                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 text-ink text-[10px]"
                                                  title={motivo}
                                                >
                                                  <span className="font-semibold text-ink-muted">Motivo:</span>
                                                  <span className="truncate max-w-[200px]">{motivo}</span>
                                                </span>
                                              )}
                                              {/* Chip de aprobador SOLO si hay traza
                                                  registrada. Si no se sabe quién
                                                  aprobó (eventos viejos sin tag), no
                                                  mostramos nada — el estado ya se
                                                  ve en el chip de Aprobado/Rechazado/
                                                  Pendiente de arriba. */}
                                              {showApprover && apprText && status === APPROVAL_STATUS.APPROVED && (
                                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-success/10 text-success text-[10px] font-semibold">
                                                  <ShieldCheck size={10} />
                                                  {apprText}
                                                </span>
                                              )}
                                              {showApprover && apprText && status === APPROVAL_STATUS.REJECTED && (
                                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-danger/10 text-danger text-[10px] font-semibold">
                                                  <ShieldCheck size={10} />
                                                  {apprText}
                                                </span>
                                              )}
                                              {modText && (
                                                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-violet-100 text-violet-800 text-[10px] font-semibold">
                                                  <Pencil size={10} />
                                                  {modText}
                                                </span>
                                              )}
                                              {recoverable === true && (
                                                <span
                                                  className="inline-flex items-center px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 text-[10px] font-bold uppercase tracking-wider"
                                                  title={isHourlyAbsence
                                                    ? 'Ausencia por horas: se recupera. La jornada del día no se reduce, así que las horas ausentes salen como deuda en la bolsa.'
                                                    : 'Las horas de esta ausencia se descontaron de la bolsa: el trabajador las debe a la empresa.'}
                                                >
                                                  Recuperable
                                                </span>
                                              )}
                                              {recoverable === false && (
                                                <span
                                                  className="inline-flex items-center px-1.5 py-0.5 rounded bg-success/10 text-success text-[10px] font-bold uppercase tracking-wider"
                                                  title="La empresa asume la ausencia: no se descontó de la bolsa de horas."
                                                >
                                                  No recuperable
                                                </span>
                                              )}
                                              {canEdit && (
                                                <span className="ml-auto inline-flex items-center gap-0.5">
                                                  <button
                                                    onClick={(ev) => { ev.stopPropagation(); setEditingEvent({ event: e, workerId: r.id }); }}
                                                    className="text-ink-muted hover:text-primary-darker p-1 rounded transition shrink-0"
                                                    title="Editar hora y tipo de este fichaje"
                                                  >
                                                    <Pencil size={11} />
                                                  </button>
                                                  <button
                                                    onClick={(ev) => { ev.stopPropagation(); setDeleteState({ events: [e], workerId: r.id, workerName: r.worker?.name }); }}
                                                    className="text-red-500 hover:text-red-700 p-1 rounded transition shrink-0"
                                                    title="Eliminar este fichaje"
                                                  >
                                                    <Trash2 size={11} />
                                                  </button>
                                                </span>
                                              )}
                                            </li>
                                          );
                                        })}
                                      </ul>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      </div>

      <EventEditModal
        open={!!editingEvent}
        event={editingEvent?.event}
        // Fecha/hora del fichaje en la zona de SU trabajador.
        timeZone={timeZoneForWorker(workerById.get(Number(editingEvent?.workerId)))}
        onClose={() => setEditingEvent(null)}
        onSaved={handleEdited}
      />

      <DeleteEventsConfirmModal
        open={!!deleteState}
        events={deleteState?.events || []}
        workerName={deleteState?.workerName}
        onClose={() => setDeleteState(null)}
        onDeleted={handleDeleted}
      />
    </div>
  );
};

export default InformeHorarioView;
